<script setup lang="ts">
import { defineProps } from 'vue';

interface Props {
  /** Адрес «на уровень выше»: /lk/* → /lk, сам /lk → /. */
  to?: string;
}
const props = defineProps<Props>();

const router = useRouter();

/**
 * Переход на уровень выше.
 *
 * Раньше кнопка звала `router.back()` всегда, когда в истории было больше одной
 * записи, а заданный `to` использовался только как запасной вариант — то есть
 * практически никогда. Получалась не «вверх», а «назад в браузере»: походив
 * между разделами личного кабинета, в неё можно было попасть в петлю
 * (адреса → безопасность → адреса → …) и не выбраться на главную вовсе.
 *
 * Теперь `to` — обещание, а не запасной путь: у всех кнопок в проекте он задан
 * осознанно и указывает на родительский экран. Браузерное «назад» никуда
 * не делось — оно есть в самом браузере и в жесте на телефоне.
 */
const goBack = () => {
  if (props.to) {
    router.push(props.to);
    return;
  }

  // Без явного адреса поведение прежнее
  if (globalThis.history?.length > 1) {
    router.back();
  } else {
    router.push('/');
  }
};
</script>

<template>
  <button type="button" class="lk-header-icon mr-auto mb-5" aria-label="Назад" @click="goBack">
    <!-- Стрелка прорисовывается штрихом при появлении экрана — как остальные
         иконки личного кабинета. `mode="svg"`: в CSS-режиме иконка становится
         маской, а SVG внутри маски браузер не анимирует. -->
    <Icon mode="svg" name="line-md:arrow-left" class="w-6 h-6" />
  </button>
</template>

<style scoped lang="scss">
.lk-header-icon {
  @apply relative w-12 h-12 rounded-full;
  @apply bg-white/20 backdrop-blur-md;
  @apply border border-white/30;
  @apply flex items-center justify-center;
  @apply text-black dark:text-white;
  @apply transition-all duration-300;
  @apply hover:bg-white/30 hover:scale-110;
  @apply active:scale-95;
  @apply cursor-pointer;
  @apply dark:text-white;
}
</style>
